import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Campaign as AdsIcon,
  Cookie as CookieIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import CookieBanner from '../components/CookieBanner';
import CookieModal from '../components/CookieModal';
import './Cookies.css';

const Cookies = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    performance: false,
    functionality: false,
    advertising: false,
  });

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà fait un choix
    const savedPreferences = localStorage.getItem('cookiePreferences');
    if (!savedPreferences) {
      setShowBanner(true);
    } else {
      setCookiePreferences(JSON.parse(savedPreferences));
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      performance: true,
      functionality: true,
      advertising: true,
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    setShowBanner(false);
    setSnackbarMessage('Tous les cookies ont été acceptés');
    setSnackbarOpen(true);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      performance: false,
      functionality: false,
      advertising: false,
    };
    setCookiePreferences(onlyNecessary);
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyNecessary));
    setShowBanner(false);
    setSnackbarMessage('Seuls les cookies nécessaires ont été acceptés');
    setSnackbarOpen(true);
  };

  const handleCustomize = () => {
    setShowModal(true);
  };

  const handleSavePreferences = (preferences) => {
    setCookiePreferences(preferences);
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    setShowModal(false);
    setShowBanner(false);
    setSnackbarMessage('Vos préférences ont été enregistrées');
    setSnackbarOpen(true);
  };

  const cookieTypes = [
    {
      id: 'necessary',
      title: 'Cookies strictement nécessaires',
      description: 'Ces cookies sont essentiels au fonctionnement du site. Ils permettent la navigation, l\'authentification et la sécurité.',
      icon: <SecurityIcon />,
      color: '#e74c3c',
      examples: 'Session utilisateur, sécurité CSRF, préférences de langue',
      required: true,
    },
    {
      id: 'performance',
      title: 'Cookies de performance',
      description: 'Ces cookies nous aident à comprendre comment les visiteurs interagissent avec notre site en collectant des informations anonymes.',
      icon: <AnalyticsIcon />,
      color: '#3498db',
      examples: 'Google Analytics, statistiques de visite, temps de chargement',
      required: false,
    },
    {
      id: 'functionality',
      title: 'Cookies de fonctionnalité',
      description: 'Ces cookies permettent au site de se souvenir de vos choix et de vous offrir des fonctionnalités améliorées.',
      icon: <SettingsIcon />,
      color: '#27ae60',
      examples: 'Préférences utilisateur, thème sombre/clair, langue',
      required: false,
    },
    {
      id: 'advertising',
      title: 'Cookies publicitaires',
      description: 'Ces cookies sont utilisés pour vous proposer des publicités pertinentes et mesurer l\'efficacité de nos campagnes.',
      icon: <AdsIcon />,
      color: '#f39c12',
      examples: 'Google Ads, Facebook Pixel, partenaires publicitaires',
      required: false,
    },
  ];

  const faqItems = [
    {
      question: 'Comment supprimer les cookies de mon navigateur ?',
      answer: 'Vous pouvez supprimer les cookies directement depuis les paramètres de votre navigateur. Dans Chrome : Paramètres > Confidentialité et sécurité > Cookies et autres données de sites. Dans Firefox : Paramètres > Vie privée et sécurité > Cookies et données de sites.',
    },
    {
      question: 'Puis-je retirer mon consentement à tout moment ?',
      answer: 'Oui, conformément au RGPD, vous pouvez retirer votre consentement à tout moment en revenant sur cette page et en modifiant vos préférences.',
    },
    {
      question: 'Que se passe-t-il si je refuse tous les cookies ?',
      answer: 'Si vous refusez tous les cookies non-essentiels, certaines fonctionnalités du site peuvent être limitées, mais vous pourrez toujours naviguer et utiliser les services principaux.',
    },
    {
      question: 'Mission Sport partage-t-il mes données avec des tiers ?',
      answer: 'Nous ne partageons vos données personnelles qu\'avec nos partenaires de confiance et uniquement dans le cadre de nos services. Consultez notre Politique de confidentialité pour plus de détails.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Cookies | Mission Sport</title>
        <meta
          name="description"
          content="Découvrez comment Mission Sport utilise les cookies pour améliorer votre expérience. Gérez vos préférences et consultez notre politique de cookies."
        />
        <meta property="og:title" content="Politique de Cookies - Mission Sport" />
        <meta
          property="og:description"
          content="Gérez vos préférences de cookies sur Mission Sport. Transparence et contrôle total sur vos données."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <main className="cookies-page">
        {/* Header Section */}
        <Box className="cookies-header">
          <Container maxWidth="lg">
            <Box className="cookies-breadcrumb">
              <Typography variant="body2" color="text.secondary">
                Accueil / Légal / Cookies
              </Typography>
            </Box>
            <Box className="cookies-header-content">
              <CookieIcon className="cookies-header-icon" />
              <Typography variant="h1" className="cookies-title">
                Politique de Cookies
              </Typography>
              <Typography variant="h6" className="cookies-subtitle">
                Mission Sport utilise des cookies pour améliorer votre expérience utilisateur,
                sécuriser notre plateforme et vous proposer des services personnalisés.
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Explanation Section */}
        <Container maxWidth="lg" className="cookies-container">
          <section className="cookies-explanation">
            <Box className="section-header">
              <InfoIcon className="section-icon" />
              <Typography variant="h2" className="section-title">
                Pourquoi utilisons-nous des cookies ?
              </Typography>
            </Box>
            <Typography variant="body1" className="explanation-text">
              Les cookies sont de petits fichiers texte stockés sur votre appareil lorsque vous visitez notre site.
              Ils nous permettent de reconnaître votre navigateur, de mémoriser vos préférences et d'améliorer
              continuellement nos services. Chez Mission Sport, nous utilisons les cookies pour :
            </Typography>
            <Grid container spacing={2} className="explanation-benefits">
              <Grid item xs={12} md={6}>
                <Box className="benefit-item">
                  <SecurityIcon className="benefit-icon" />
                  <Typography variant="h6">Sécurité et authentification</Typography>
                  <Typography variant="body2">
                    Maintenir votre session connectée et protéger votre compte
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box className="benefit-item">
                  <AnalyticsIcon className="benefit-icon" />
                  <Typography variant="h6">Analyse et amélioration</Typography>
                  <Typography variant="body2">
                    Comprendre l'usage du site pour l'optimiser
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </section>

          {/* Cookie Types Section */}
          <section className="cookies-types">
            <Typography variant="h2" className="section-title">
              Types de cookies utilisés
            </Typography>
            <Grid container spacing={3}>
              {cookieTypes.map((type) => (
                <Grid item xs={12} md={6} key={type.id}>
                  <Card className="cookie-type-card">
                    <CardContent>
                      <Box className="cookie-type-header">
                        <Box
                          className="cookie-type-icon"
                          sx={{ backgroundColor: type.color }}
                        >
                          {type.icon}
                        </Box>
                        <Box className="cookie-type-info">
                          <Typography variant="h6" className="cookie-type-title">
                            {type.title}
                          </Typography>
                          {type.required && (
                            <Chip
                              label="Obligatoire"
                              size="small"
                              color="error"
                              className="required-chip"
                            />
                          )}
                        </Box>
                      </Box>
                      <Typography variant="body2" className="cookie-type-description">
                        {type.description}
                      </Typography>
                      <Typography variant="caption" className="cookie-type-examples">
                        <strong>Exemples :</strong> {type.examples}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </section>

          {/* Cookie Management Section */}
          <section className="cookies-management">
            <Typography variant="h2" className="section-title">
              Gérer vos préférences
            </Typography>
            <Card className="management-card">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Contrôlez vos cookies
                </Typography>
                <Typography variant="body2" className="management-description">
                  Vous avez le contrôle total sur les cookies que nous utilisons.
                  Personnalisez vos préférences selon vos besoins.
                </Typography>
                <Box className="management-actions">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAcceptAll}
                    className="management-button"
                  >
                    Accepter tous les cookies
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleRejectAll}
                    className="management-button"
                  >
                    Refuser les cookies non-essentiels
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleCustomize}
                    className="management-button"
                  >
                    Personnaliser mes préférences
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </section>

          {/* FAQ Section */}
          <section className="cookies-faq">
            <Typography variant="h2" className="section-title">
              Questions fréquentes
            </Typography>
            {faqItems.map((item, index) => (
              <Accordion key={index} className="faq-accordion">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">{item.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">{item.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </section>

          {/* Contact Section */}
          <section className="cookies-contact">
            <Alert severity="info" className="contact-alert">
              <Typography variant="body2">
                <strong>Besoin d'aide ?</strong> Si vous avez des questions concernant notre utilisation des cookies
                ou si vous souhaitez exercer vos droits RGPD,{' '}
                <Button
                  component="a"
                  href="/contact"
                  variant="text"
                  color="primary"
                  size="small"
                >
                  contactez-nous
                </Button>
                .
              </Typography>
            </Alert>
          </section>
        </Container>
      </main>

      {/* Cookie Banner */}
      {showBanner && (
        <CookieBanner
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onCustomize={handleCustomize}
        />
      )}

      {/* Cookie Modal */}
      <CookieModal
        open={showModal}
        onClose={() => setShowModal(false)}
        cookieTypes={cookieTypes}
        preferences={cookiePreferences}
        onSave={handleSavePreferences}
      />

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );
};

export default Cookies;